import { AdminAssessmentStorage, ASSESSMENT_CONTENT_UPDATED_EVENT } from '../AdminAssessmentStorage';
import type { AdminAssessmentDto, AdminAssessmentReportDto } from '../../types/adminAssessment';
import type { PatinfoHistoryDto } from '../../types/api/patinfo';
import { normalizeHistoryAdminResponse } from '../../types/api/patinfo';
import type { AccountPlaDto } from '../../types/api/service';
import type { AdminResponseDto } from '../../types/AdminResponseDto';

/** Re-exported so the Clients views can subscribe without a second import path. */
export { ASSESSMENT_CONTENT_UPDATED_EVENT };

/**
 * Same global-registry fallback ProfileRowEditor.tsx uses elsewhere in this file for its own
 * caches (toggle arrows, PDF icon) — duplicated here (not imported) since it's a tiny,
 * stateless lookup, to avoid an awkward cross-import for four lines.
 */
const resolveRuntimeStorage = () => {
  return (globalThis as any).__proGroupsStorage
    ?? (globalThis as any).__childNavStorage
    ?? null;
};

/**
 * Assessment definitions (content, not per-profile data) are the same for every caller on
 * the page — load once per module (not once per row/component instance) and cache. Shared
 * by ProfileRowEditor.tsx (Reports table / row-header PDF icon) and
 * RecentReportsResultsList.tsx (per-row PDF icon), so both consume the same cache/network
 * call rather than each loading their own copy.
 */
let cachedAssessmentsBySourceId: Map<string, AdminAssessmentDto> | null = null;
let assessmentsLoadPromise: Promise<Map<string, AdminAssessmentDto>> | null = null;
// Bumped on every invalidation so an in-flight load that started before the invalidation
// doesn't overwrite the (now-cleared) cache with the data it read pre-invalidation.
let cacheGeneration = 0;

export function getCachedAssessmentsBySourceId(): Map<string, AdminAssessmentDto> | null {
  return cachedAssessmentsBySourceId;
}

/**
 * Drops the in-memory assessment-definition cache so the next loadAssessmentsBySourceId() call
 * re-reads storage. Wired to AdminAssessmentStorage's ASSESSMENT_CONTENT_UPDATED_EVENT below,
 * so bundle-content unpack / debug download / admin-result save all clear it automatically —
 * previously this cache was populated once (often with an empty result, when the Clients views
 * mounted before cold-start bundle unpacking finished) and never refreshed for the life of the
 * page, leaving history rows with no assessment name / reports dropdown / view icons.
 */
export function invalidateAssessmentsBySourceIdCache(): void {
  cachedAssessmentsBySourceId = null;
  assessmentsLoadPromise = null;
  cacheGeneration += 1;
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener(ASSESSMENT_CONTENT_UPDATED_EVENT, () => {
    invalidateAssessmentsBySourceIdCache();
  });
}

export async function loadAssessmentsBySourceId(forceRefresh = false): Promise<Map<string, AdminAssessmentDto>> {
  if (forceRefresh) {
    invalidateAssessmentsBySourceIdCache();
  }

  if (cachedAssessmentsBySourceId) {
    return cachedAssessmentsBySourceId;
  }

  if (assessmentsLoadPromise) {
    return assessmentsLoadPromise;
  }

  const generationAtStart = cacheGeneration;
  assessmentsLoadPromise = (async () => {
    try {
      const storage = resolveRuntimeStorage();
      const map = storage
        ? new Map<string, AdminAssessmentDto>(
            (await new AdminAssessmentStorage(storage).getAvailableAssessments()).map((a) => [a.sourceId, a])
          )
        : new Map<string, AdminAssessmentDto>();

      // Only commit to the module cache if nothing invalidated it while this load was running.
      if (cacheGeneration === generationAtStart) {
        cachedAssessmentsBySourceId = map;
      }
      return map;
    } catch (error) {
      // Don't cache failures — let the next call retry rather than pinning an empty map.
      console.error('ClientReportAssessments: failed to load assessment definitions', error);
      return new Map<string, AdminAssessmentDto>();
    } finally {
      assessmentsLoadPromise = null;
    }
  })();

  return assessmentsLoadPromise;
}

/** Resolves which AdminAssessmentDto (and its reports) a history entry's RecAssessment points to. */
export function resolveHistoryItemReport(
  historyItem: PatinfoHistoryDto,
  assessmentsBySourceId: Map<string, AdminAssessmentDto>
): { assessmentDefinition: AdminAssessmentDto | null; reports: AdminAssessmentReportDto[] } {
  const assessmentDefinition = assessmentsBySourceId.get(historyItem.Admin?.RecAssessment) ?? null;
  return { assessmentDefinition, reports: assessmentDefinition?.reports ?? [] };
}

/**
 * Resolves the account's configured "Default Report" (Account > Assessment Options > a PLA's
 * Reports setup — see AssessmentOptionsView.tsx's "Default Report" dropdown) for the given
 * assessment, if one was ever set. Matches the PLA by AssessmentId === assessmentDefinition.
 * sourceId (the same content-template ↔ account-settings link AssessmentOptionsView.tsx uses)
 * and reads the 'defaultreportid' preload off it (AccountPlaDto.Preloads — a generic
 * {FieldCode, Value} array, same convention as the pre-existing 'adminreportid' preload driving
 * AdminView's own end-of-administration renderReport(getPreload('adminreportid'))).
 *
 * Returns undefined (not a report) when nothing's configured, the PLA can't be found, or the
 * configured reportId no longer exists on this assessment (e.g. content changed after the PLA
 * was saved) — callers should fall back to their own default (typically reports[0]) in that case.
 */
export function resolveDefaultReportId(
  assessmentDefinition: AdminAssessmentDto | null,
  assessmentOptions: AccountPlaDto[]
): string | undefined {
  if (!assessmentDefinition) {
    return undefined;
  }
  const pla = assessmentOptions.find((p) => p.AssessmentId === assessmentDefinition.sourceId);
  const configuredReportId = pla?.Preloads?.find((p) => p.FieldCode === 'defaultreportid')?.Value;
  if (!configuredReportId) {
    return undefined;
  }
  const stillExists = (assessmentDefinition.reports ?? []).some((r) => r.reportId === configuredReportId);
  return stillExists ? configuredReportId : undefined;
}

/** recUTM wins as the comparison timestamp (finer-grained); recDate is the fallback when recUTM
 * is blank/unparseable. Returns NaN when neither parses — callers drop those entries. */
function getComparableTimestamp(entry: AdminResponseDto): number {
  const utm = Date.parse(entry.recUTM || '');
  if (Number.isFinite(utm)) return utm;
  return Date.parse(entry.recDate || '');
}

/**
 * Shared filter/sort behind resolveTrendHistoryFromEmbedded/resolveTrendHistoryFromLocal — see
 * either for the public contract. Filters `candidates` to the same recAssessment as `current`,
 * drops `current` itself out of that set (matched by recId, falling back to (id, recUTM) when
 * recId is blank — mirrors AdminResponseStorage's own dedupe convention) and any entry whose
 * comparable timestamp can't be parsed or is strictly newer than current's own ("newer
 * assessments are ignored"), sorts the remainder oldest→newest, then appends `current` as the
 * final element — guaranteeing it's included and always last, whether or not a matching entry
 * existed among candidates.
 */
function resolveTrendHistory(
  current: AdminResponseDto,
  candidates: AdminResponseDto[]
): AdminResponseDto[] {
  const currentTs = getComparableTimestamp(current);
  const cutoff = Number.isFinite(currentTs) ? currentTs : Infinity;
  const isCurrentEntry = (c: AdminResponseDto): boolean =>
    current.recId && c.recId
      ? c.recId === current.recId
      : c.id === current.id && (c.recUTM || '') === (current.recUTM || '');

  const filtered = candidates
    .filter((c) => c.recAssessment === current.recAssessment && !isCurrentEntry(c))
    .map((c) => ({ entry: c, ts: getComparableTimestamp(c) }))
    .filter((x) => Number.isFinite(x.ts) && x.ts <= cutoff)
    .sort((a, b) => a.ts - b.ts)
    .map((x) => x.entry);

  return [...filtered, current];
}

/**
 * Resolves trend-chart history (see reportRenderer.ts's parseTrendChartPreloads/
 * buildTrendChartOverrides) from a Client profile's embedded, unfiltered search-result history
 * (PatinfoProfileDto.history — any order, every assessmentId mixed together). Normalizes each
 * entry via the existing normalizeHistoryAdminResponse before filtering/sorting.
 *
 * Returns every historical administration of `currentAdminResponse.recAssessment`, oldest→
 * newest, capped at (and always including, even if absent from `embeddedHistory`)
 * currentAdminResponse itself.
 */
export function resolveTrendHistoryFromEmbedded(
  currentAdminResponse: AdminResponseDto,
  embeddedHistory: PatinfoHistoryDto[] | undefined
): AdminResponseDto[] {
  const normalized = (embeddedHistory ?? []).map((h) => normalizeHistoryAdminResponse(h.Admin));
  return resolveTrendHistory(currentAdminResponse, normalized);
}

/**
 * Same as resolveTrendHistoryFromEmbedded, but for the on-device flow (WellVisitView/respondent
 * sessions) where history comes from local AdminResponseStorage.getProfileResponses(profileId)
 * (already AdminResponseDto[], every assessmentId mixed together — no normalization needed).
 */
export function resolveTrendHistoryFromLocal(
  currentAdminResponse: AdminResponseDto,
  localResponses: AdminResponseDto[] | undefined
): AdminResponseDto[] {
  return resolveTrendHistory(currentAdminResponse, localResponses ?? []);
}
